import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { ShareLinkView, ShareMode, ShareStats } from '@/contracts/sharing';
import * as api from '@/features/sharing/api/sharing-api';

export function ShareSettingsPanel({
  funnelId,
  onClose,
  flush,
}: {
  funnelId: string;
  onClose: () => void;
  flush?: () => Promise<boolean>;
}) {
  const [share, setShare] = useState<ShareLinkView | null>(null);
  const [mode, setMode] = useState<ShareMode>('private_link');
  const [password, setPassword] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [stats, setStats] = useState<ShareStats | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void api.getShare(funnelId).then(({ share: value }) => {
      setShare(value);
      if (value) setMode(value.mode);
    });
  }, [funnelId]);

  async function save() {
    setMessage(null);
    try {
      if (flush && !(await flush())) {
        setMessage('Não foi possível salvar as últimas alterações do funil. Tente novamente antes de habilitar o compartilhamento.');
        return;
      }
      const result = await api.saveShare(
        funnelId,
        { mode, ...(mode === 'password' && password ? { password } : {}) },
        Boolean(share),
      );
      setShare(result.share);
      setPassword('');
      if (result.share.shareUrl) setShareUrl(result.share.shareUrl);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Não foi possível salvar o compartilhamento.');
    }
  }
  async function regenerate() {
    if (!confirm('O link atual deixará de funcionar.')) return;
    const result = await api.regenerateShare(funnelId);
    setShare(result.share);
    setShareUrl(result.share.shareUrl ?? null);
  }
  async function disable() {
    if (!confirm('Qualquer pessoa que usar este link perderá o acesso.')) return;
    await api.disableShare(funnelId);
    setShare(share ? { ...share, enabled: false, status: 'revoked' } : null);
    setShareUrl(null);
    setQr(null);
  }

  async function copyLink() {
    if (!shareUrl) return;
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl);
      return;
    }
    const input = document.createElement('textarea');
    input.value = shareUrl;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input as never);
    input.select();
    document.execCommand('copy');
    input.remove();
  }

  return (
    <aside
      aria-label="Configurações de compartilhamento"
      className="flex h-full w-[380px] flex-col gap-3 overflow-y-auto border-l border-border-subtle bg-surface p-4 text-sm"
    >
      <div className="flex justify-between">
        <h2 className="font-semibold">Compartilhar</h2>
        <button onClick={onClose}>Fechar</button>
      </div>
      <p className="text-xs text-text-muted">Status: {share?.status ?? 'desabilitado'}</p>
      <label>
        Modo{' '}
        <select
          aria-label="Modo de compartilhamento"
          value={mode}
          onChange={(e) => setMode(e.target.value as ShareMode)}
          className="rounded bg-bg-app p-1"
        >
          <option value="private_link">Link privado</option>
          <option value="public">Público</option>
          <option value="password">Senha</option>
        </select>
      </label>
      {mode === 'password' ? (
        <label>
          Senha{' '}
          <input
            aria-label="Senha de compartilhamento"
            type="password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded bg-bg-app p-1"
          />
        </label>
      ) : null}
      <button className="rounded border border-border-brand p-2" onClick={() => void save()}>
        {share ? 'Atualizar compartilhamento' : 'Habilitar compartilhamento'}
      </button>
      {shareUrl ? (
        <>
          <input
            aria-label="URL de compartilhamento"
            readOnly
            value={shareUrl}
            className="rounded bg-bg-app p-2"
          />
          <div className="flex gap-2">
            <button onClick={() => void copyLink()}>Copiar link</button>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer">
              Abrir link
            </a>
            <button onClick={() => void QRCode.toDataURL(shareUrl, { width: 320 }).then(setQr)}>
              Código QR
            </button>
          </div>
        </>
      ) : share ? (
        <p className="text-xs text-text-muted">Gere o link novamente para exibir uma nova URL copiável.</p>
      ) : null}
      {qr ? (
        <a href={qr} download="dark-funnels-share-qr.png">
          <img src={qr} alt="Código QR de compartilhamento" className="w-48" />
          Baixar QR
        </a>
      ) : null}
      {share ? (
        <div className="flex gap-3">
          <button onClick={() => void regenerate()}>Gerar link novamente</button>
          <button onClick={() => void disable()}>Desabilitar compartilhamento</button>
          <button onClick={() => void api.getShareStats(funnelId).then((r) => setStats(r.stats))}>
            Ver estatísticas
          </button>
        </div>
      ) : null}
      {stats ? (
        <div aria-label="Estatísticas de compartilhamento">
          Visualizações: {stats.totalViews} · Sessões únicas: {stats.approximateUniqueSessions} · Última
          visualização: {stats.lastViewedAt ?? 'Nunca'}
        </div>
      ) : null}
      {message ? (
        <p role="alert" className="text-[#f08484]">
          {message}
        </p>
      ) : null}
    </aside>
  );
}
